import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { MouseButtonMap, User } from '../types';
import { useExperience } from '../content/ExperienceContext';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, displayName: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (profile: { displayName?: string; avatarData?: string; bio?: string }) => Promise<void>;
  updateEditorPreferences: (editorMouseSettings: MouseButtonMap) => Promise<void>;
  redeemCode: (code: string) => Promise<{ durationDays: number; professionalExpiresAt: number; message: string }>;
  refreshUser: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(options?.headers || {}) }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || '请求失败');
    return data;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw new Error('账号服务响应超时');
    throw error;
  } finally {
    window.clearTimeout(timer);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { locale } = useExperience();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const data = await request<{ user: User | null }>(`/api/auth/me${locale ? `?locale=${encodeURIComponent(locale)}` : ''}`);
      setUser(data.user);
      return data.user;
    } catch {
      setUser(null);
      return null;
    }
  }, [locale]);

  useEffect(() => {
    void refreshUser().finally(() => setLoading(false));
    const onFocus = () => { void refreshUser(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshUser]);

  const login = useCallback(async (username: string, password: string) => {
    const data = await request<{ user: User }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password })
    });
    setUser(data.user);
  }, []);

  const register = useCallback(async (username: string, displayName: string, password: string) => {
    const data = await request<{ user: User }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, displayName, password })
    });
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    await request('/api/auth/logout', { method: 'POST' });
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (profile: { displayName?: string; avatarData?: string; bio?: string }) => {
    const data = await request<{ user: User }>('/api/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(profile)
    });
    setUser(data.user);
  }, []);

  const updateEditorPreferences = useCallback(async (editorMouseSettings: MouseButtonMap) => {
    const data = await request<{ user: User }>('/api/auth/editor-preferences', {
      method: 'PATCH',
      body: JSON.stringify({ editorMouseSettings })
    });
    setUser(data.user);
  }, []);

  const redeemCode = useCallback(async (code: string) => {
    const data = await request<{ user: User; durationDays: number; professionalExpiresAt: number; message: string }>('/api/membership/redeem', {
      method: 'POST',
      body: JSON.stringify({ code })
    });
    setUser(data.user);
    return { durationDays: data.durationDays, professionalExpiresAt: data.professionalExpiresAt, message: data.message };
  }, []);

  const value = useMemo(() => ({ user, loading, login, register, logout, updateProfile, updateEditorPreferences, redeemCode, refreshUser }), [user, loading, login, register, logout, updateProfile, updateEditorPreferences, redeemCode, refreshUser]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
